import React, { useState, useEffect, useRef } from 'react';
import { 
    Upload, Trash2, Users, FileSpreadsheet, ArrowLeft, Check, X, AlertCircle,
    Search, UserPlus, ToggleLeft, ToggleRight, Clock, Activity, ChevronLeft, 
    ChevronRight, Eye, UserX, UserCheck, RefreshCw, Download, ArrowUpDown, ArrowUp, ArrowDown, Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = ({ adminCode, onBack }) => {
    const [activeTab, setActiveTab] = useState('users');
    const [codes, setCodes] = useState([]);
    const [stats, setStats] = useState(null);
    const [loginLogs, setLoginLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [logsPage, setLogsPage] = useState(1);
    const [logsTotalPages, setLogsTotalPages] = useState(1);
    const fileInputRef = useRef(null);
    
    // Sort state
    const [sortBy, setSortBy] = useState('last_login');
    const [sortDir, setSortDir] = useState(-1);
    
    // New user form
    const [showAddUser, setShowAddUser] = useState(false);
    const [newUser, setNewUser] = useState({ code: '', email: '', name: '' });
    const [addingUser, setAddingUser] = useState(false);

    // Chat logs state
    const [chatLogs, setChatLogs] = useState([]);
    const [chatLogsPage, setChatLogsPage] = useState(1);
    const [chatLogsTotalPages, setChatLogsTotalPages] = useState(1);
    const [chatLogsSearch, setChatLogsSearch] = useState('');

    // Suggestions state
    const [suggestions, setSuggestions] = useState([]);
    const [newSuggestion, setNewSuggestion] = useState('');
    const [addingSuggestion, setAddingSuggestion] = useState(false);

    // Chat log moderation
    const [logToDelete, setLogToDelete] = useState(null);

    const fetchStats = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/users/stats?admin_code=${adminCode}`);
            const data = await response.json();
            if (response.ok) {
                setStats(data);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const fetchCodes = async (page = 1, search = '', sort = sortBy, dir = sortDir) => {
        setLoading(true);
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const response = await fetch(`${API_URL}/api/admin/codes?admin_code=${adminCode}&page=${page}&limit=20${searchParam}&sort_by=${sort}&sort_dir=${dir}`);
            const data = await response.json();
            
            if (response.ok) {
                setCodes(data.codes || []);
                setTotalPages(data.pages || 1);
                setCurrentPage(data.page || 1);
            } else {
                setError(data.detail || 'Failed to fetch codes');
            }
        } catch (err) {
            setError('Connection error');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        const newDir = sortBy === field ? -sortDir : -1;
        setSortBy(field);
        setSortDir(newDir);
        fetchCodes(1, searchTerm, field, newDir);
    };

    const SortIcon = ({ field }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
        return sortDir === 1 
            ? <ArrowUp className="w-3 h-3 ml-1 text-primary" /> 
            : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
    };

    const fetchLoginLogs = async (page = 1) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/login-logs?admin_code=${adminCode}&page=${page}&limit=20`);
            const data = await response.json();
            
            if (response.ok) {
                setLoginLogs(data.logs || []);
                setLogsTotalPages(data.pages || 1);
                setLogsPage(data.page || 1);
            }
        } catch (err) {
            console.error('Error fetching logs:', err);
        }
    };

    const fetchChatLogs = async (page = 1, search = '') => {
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const response = await fetch(`${API_URL}/api/admin/chat-logs?admin_code=${adminCode}&page=${page}&limit=20${searchParam}`);
            const data = await response.json();
            if (response.ok) {
                setChatLogs(data.logs || []);
                setChatLogsTotalPages(data.pages || 1);
                setChatLogsPage(data.page || 1);
            }
        } catch (err) {
            console.error('Error fetching chat logs:', err);
        }
    };

    const handleDeleteChatLog = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/chat-logs/${id}?admin_code=${adminCode}`, { method: 'DELETE' });
            if (response.ok) setChatLogs(prev => prev.filter(l => l.id !== id));
        } catch (err) {
            console.error('Error deleting chat log:', err);
        } finally {
            setLogToDelete(null);
        }
    };

    const handlePromptToSuggestion = async (text) => {
        const value = (text || '').trim();
        if (!value) return;
        setError(''); setSuccess('');
        try {
            const response = await fetch(`${API_URL}/api/admin/suggestions?admin_code=${adminCode}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: value.slice(0, 200) }),
            });
            const data = await response.json();
            if (response.ok) {
                setSuccess('Prompt añadido a sugerencias');
                fetchSuggestions();
            } else {
                setError(data.detail || 'No se pudo añadir como sugerencia');
            }
        } catch {
            setError('Error al añadir como sugerencia');
        }
    };

    const fetchSuggestions = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/suggestions?admin_code=${adminCode}`);
            const data = await response.json();
            if (response.ok) setSuggestions(data.suggestions || []);
        } catch (err) {
            console.error('Error fetching suggestions:', err);
        }
    };

    const handleAddSuggestion = async (e) => {
        e.preventDefault();
        const text = newSuggestion.trim();
        if (!text) return;
        setAddingSuggestion(true);
        setError(''); setSuccess('');
        try {
            const response = await fetch(`${API_URL}/api/admin/suggestions?admin_code=${adminCode}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text }),
            });
            const data = await response.json();
            if (response.ok) {
                setNewSuggestion('');
                setSuccess('Sugerencia añadida');
                fetchSuggestions();
            } else {
                setError(data.detail || 'No se pudo añadir la sugerencia');
            }
        } catch {
            setError('Error al añadir la sugerencia');
        } finally {
            setAddingSuggestion(false);
        }
    };

    const handleDeleteSuggestion = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/suggestions/${id}?admin_code=${adminCode}`, { method: 'DELETE' });
            if (response.ok) setSuggestions(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            console.error('Error deleting suggestion:', err);
        }
    };

    useEffect(() => {
        fetchStats();
        fetchCodes(1, '');
        fetchLoginLogs(1);
        fetchChatLogs(1, '');
        fetchSuggestions();
    }, [adminCode]);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchCodes(1, searchTerm, sortBy, sortDir);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${API_URL}/api/admin/upload-codes?admin_code=${adminCode}`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(data.message);
                fetchCodes(1, '');
                fetchStats();
            } else {
                setError(data.detail || 'Upload failed');
            }
        } catch (err) {
            setError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleToggleUser = async (userId, currentStatus) => {
        try {
            const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle?admin_code=${adminCode}`, {
                method: 'PATCH',
            });

            if (response.ok) {
                fetchCodes(currentPage, searchTerm);
                fetchStats();
                setSuccess(`User ${currentStatus ? 'disabled' : 'enabled'} successfully`);
            } else {
                const data = await response.json();
                setError(data.detail || 'Failed to update user');
            }
        } catch (err) {
            setError('Failed to update user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

        try {
            const response = await fetch(`${API_URL}/api/admin/users/${userId}?admin_code=${adminCode}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setSuccess('User deleted successfully');
                fetchCodes(currentPage, searchTerm);
                fetchStats();
            } else {
                const data = await response.json();
                setError(data.detail || 'Failed to delete user');
            }
        } catch (err) {
            setError('Delete failed');
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setAddingUser(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/api/admin/users?admin_code=${adminCode}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess('User created successfully');
                setShowAddUser(false);
                setNewUser({ code: '', email: '', name: '' });
                fetchCodes(1, '');
                fetchStats();
            } else {
                setError(data.detail || 'Failed to create user');
            }
        } catch (err) {
            setError('Failed to create user');
        } finally {
            setAddingUser(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/export-csv?admin_code=${adminCode}`);
            if (!response.ok) throw new Error('Export failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bunker_users_export.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setSuccess('CSV exported successfully');
        } catch (err) {
            setError('Failed to export CSV');
        }
    };

    const handleExportExcel = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/export-excel?admin_code=${adminCode}`);
            if (!response.ok) throw new Error('Export failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bunker_users.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setSuccess('Excel exported successfully');
        } catch (err) {
            setError('Failed to export Excel');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        const date = new Date(dateStr);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="min-h-screen bg-background p-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <Button 
                        variant="ghost" 
                        onClick={onBack}
                        className="text-foreground"
                        data-testid="admin-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to App
                    </Button>
                    <Badge variant="outline" className="text-primary border-primary" data-testid="admin-panel-badge">
                        Admin Panel
                    </Badge>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <Card className="bg-card border-border" data-testid="stat-total-users">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <Users className="w-8 h-8 text-primary" />
                                    <div>
                                        <p className="text-2xl font-bold text-foreground">{stats.total_users}</p>
                                        <p className="text-xs text-foreground/70">Total Users</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-card border-border" data-testid="stat-active-users">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <UserCheck className="w-8 h-8 text-green-500" />
                                    <div>
                                        <p className="text-2xl font-bold text-foreground">{stats.active_users}</p>
                                        <p className="text-xs text-foreground/70">Active</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-card border-border" data-testid="stat-logged-in">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <Activity className="w-8 h-8 text-blue-500" />
                                    <div>
                                        <p className="text-2xl font-bold text-foreground">{stats.users_logged_in}</p>
                                        <p className="text-xs text-foreground/70">Have Logged In</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-card border-border" data-testid="stat-recent-logins">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <Clock className="w-8 h-8 text-yellow-500" />
                                    <div>
                                        <p className="text-2xl font-bold text-foreground">{stats.recent_logins_24h}</p>
                                        <p className="text-xs text-foreground/70">Logins (24h)</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Messages */}
                {error && (
                    <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2 text-destructive">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                        <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                    </div>
                )}
                {success && (
                    <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-500">
                        <Check className="w-4 h-4" />
                        {success}
                        <button onClick={() => setSuccess('')} className="ml-auto"><X className="w-4 h-4" /></button>
                    </div>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-5 mb-6">
                        <TabsTrigger value="users">Users</TabsTrigger>
                        <TabsTrigger value="chatLogs" data-testid="chat-logs-tab">Chat Logs</TabsTrigger>
                        <TabsTrigger value="suggestions" data-testid="suggestions-tab">Suggestions</TabsTrigger>
                        <TabsTrigger value="logs">Login History</TabsTrigger>
                        <TabsTrigger value="upload">Upload</TabsTrigger>
                    </TabsList>

                    {/* Users Tab */}
                    <TabsContent value="users">
                        <Card className="bg-card border-border">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row justify-between gap-4">
                                    <CardTitle className="text-foreground">User Management</CardTitle>
                                    <div className="flex gap-2">
                                        <form onSubmit={handleSearch} className="flex gap-2">
                                            <Input
                                                placeholder="Search users..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-48"
                                                data-testid="user-search-input"
                                            />
                                            <Button type="submit" variant="secondary" size="icon" data-testid="user-search-btn">
                                                <Search className="w-4 h-4" />
                                            </Button>
                                        </form>
                                        <Button onClick={() => fetchCodes(currentPage, searchTerm)} variant="secondary" size="icon" data-testid="user-refresh-btn">
                                            <RefreshCw className="w-4 h-4" />
                                        </Button>
                                        <Button onClick={handleExportCSV} variant="secondary" data-testid="export-csv-btn">
                                            <Download className="w-4 h-4 mr-2" />
                                            Export CSV
                                        </Button>
                                        <Button onClick={handleExportExcel} variant="secondary" data-testid="export-excel-btn">
                                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                                            Export Excel
                                        </Button>
                                        <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
                                            <DialogTrigger asChild>
                                                <Button className="gradient-gold text-primary-foreground" data-testid="add-user-btn">
                                                    <UserPlus className="w-4 h-4 mr-2" />
                                                    Add User
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="bg-card border-border">
                                                <DialogHeader>
                                                    <DialogTitle className="text-foreground">Add New User</DialogTitle>
                                                    <DialogDescription>
                                                        Create a new user account for BUNKER access.
                                                    </DialogDescription>
                                                </DialogHeader>
                                                <form onSubmit={handleAddUser}>
                                                    <div className="grid gap-4 py-4">
                                                        <div className="grid gap-2">
                                                            <Label htmlFor="code">Username *</Label>
                                                            <Input
                                                                id="code"
                                                                value={newUser.code}
                                                                onChange={(e) => setNewUser({...newUser, code: e.target.value})}
                                                                placeholder="Enter username"
                                                                required
                                                                data-testid="new-user-code-input"
                                                            />
                                                        </div>
                                                        <div className="grid gap-2">
                                                            <Label htmlFor="email">Email</Label>
                                                            <Input
                                                                id="email"
                                                                type="email"
                                                                value={newUser.email}
                                                                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                                                placeholder="Enter email (optional)"
                                                                data-testid="new-user-email-input"
                                                            />
                                                        </div>
                                                        <div className="grid gap-2">
                                                            <Label htmlFor="name">Display Name</Label>
                                                            <Input
                                                                id="name"
                                                                value={newUser.name}
                                                                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                                                placeholder="Enter display name (optional)"
                                                                data-testid="new-user-name-input"
                                                            />
                                                        </div>
                                                    </div>
                                                    <DialogFooter>
                                                        <Button type="button" variant="ghost" onClick={() => setShowAddUser(false)} data-testid="add-user-cancel-btn">
                                                            Cancel
                                                        </Button>
                                                        <Button type="submit" disabled={addingUser || !newUser.code} data-testid="add-user-submit-btn">
                                                            {addingUser ? 'Creating...' : 'Create User'}
                                                        </Button>
                                                    </DialogFooter>
                                                </form>
                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {loading ? (
                                    <div className="flex justify-center py-8">
                                        <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                    </div>
                                ) : codes.length === 0 ? (
                                    <p className="text-center text-foreground/70 py-8">No users found</p>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="text-foreground/70 cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('code')} data-testid="sort-user">
                                                            <span className="flex items-center">User<SortIcon field="code" /></span>
                                                        </TableHead>
                                                        <TableHead className="text-foreground/70 hidden md:table-cell cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('email')} data-testid="sort-email">
                                                            <span className="flex items-center">Email<SortIcon field="email" /></span>
                                                        </TableHead>
                                                        <TableHead className="text-foreground/70 cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('is_active')} data-testid="sort-status">
                                                            <span className="flex items-center">Status<SortIcon field="is_active" /></span>
                                                        </TableHead>
                                                        <TableHead className="text-foreground/70 hidden sm:table-cell cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('last_login')} data-testid="sort-last-login">
                                                            <span className="flex items-center">Last Login<SortIcon field="last_login" /></span>
                                                        </TableHead>
                                                        <TableHead className="text-foreground/70 hidden sm:table-cell cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort('login_count')} data-testid="sort-logins">
                                                            <span className="flex items-center">Logins<SortIcon field="login_count" /></span>
                                                        </TableHead>
                                                        <TableHead className="text-foreground/70">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {codes.map((code) => (
                                                        <TableRow key={code.id} data-testid={`user-row-${code.id}`}>
                                                            <TableCell className="font-medium text-foreground">
                                                                {code.code}
                                                                {code.is_admin && (
                                                                    <Badge className="ml-2 bg-primary/20 text-primary border-0 text-xs">Admin</Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-foreground/70 hidden md:table-cell">
                                                                {code.email || '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {code.is_active ? (
                                                                    <Badge className="bg-green-500/20 text-green-500 border-0" data-testid={`user-status-${code.id}`}>Active</Badge>
                                                                ) : (
                                                                    <Badge className="bg-red-500/20 text-red-500 border-0" data-testid={`user-status-${code.id}`}>Disabled</Badge>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-foreground/70 text-sm hidden sm:table-cell">
                                                                {formatDate(code.last_login)}
                                                            </TableCell>
                                                            <TableCell className="text-foreground/70 hidden sm:table-cell">
                                                                {code.login_count || 0}
                                                            </TableCell>
                                                            <TableCell>
                                                                {!code.is_admin && (
                                                                    <div className="flex gap-1">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            onClick={() => handleToggleUser(code.id, code.is_active)}
                                                                            className={code.is_active ? "text-yellow-500 hover:bg-yellow-500/10" : "text-green-500 hover:bg-green-500/10"}
                                                                            title={code.is_active ? "Disable user" : "Enable user"}
                                                                            data-testid={`toggle-user-${code.id}`}
                                                                        >
                                                                            {code.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            onClick={() => handleDeleteUser(code.id)}
                                                                            className="text-destructive hover:bg-destructive/10"
                                                                            title="Delete user"
                                                                            data-testid={`delete-user-${code.id}`}
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                        
                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="flex justify-center items-center gap-2 mt-4">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fetchCodes(currentPage - 1, searchTerm)}
                                                    disabled={currentPage <= 1}
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </Button>
                                                <span className="text-sm text-foreground/70">
                                                    Page {currentPage} of {totalPages}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fetchCodes(currentPage + 1, searchTerm)}
                                                    disabled={currentPage >= totalPages}
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Chat Logs Tab */}
                    <TabsContent value="chatLogs">
                        <Card className="bg-card border-border">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-foreground text-lg">Chat Prompt Logs</CardTitle>
                                    <form onSubmit={(e) => { e.preventDefault(); fetchChatLogs(1, chatLogsSearch); }} className="flex gap-2">
                                        <Input
                                            placeholder="Search user or prompt..."
                                            value={chatLogsSearch}
                                            onChange={(e) => setChatLogsSearch(e.target.value)}
                                            className="w-48"
                                            data-testid="chat-logs-search-input"
                                        />
                                        <Button type="submit" variant="secondary" size="icon" data-testid="chat-logs-search-btn">
                                            <Search className="w-4 h-4" />
                                        </Button>
                                    </form>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-foreground/70">User</TableHead>
                                            <TableHead className="text-foreground/70">Prompt</TableHead>
                                            <TableHead className="text-foreground/70 hidden md:table-cell">Response</TableHead>
                                            <TableHead className="text-foreground/70">Time</TableHead>
                                            <TableHead className="text-foreground/70 text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {chatLogs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center text-foreground/50 py-8">
                                                    No chat logs yet
                                                </TableCell>
                                            </TableRow>
                                        ) : chatLogs.map((log, i) => (
                                            <TableRow key={i} data-testid={`chat-log-row-${i}`}>
                                                <TableCell className="font-medium text-foreground whitespace-nowrap">
                                                    {log.user_code}
                                                </TableCell>
                                                <TableCell className="text-foreground/80 max-w-[200px] truncate">
                                                    {log.question}
                                                </TableCell>
                                                <TableCell className="text-foreground/60 max-w-[250px] truncate hidden md:table-cell text-xs">
                                                    {log.response}
                                                </TableCell>
                                                <TableCell className="text-foreground/70 text-sm whitespace-nowrap">
                                                    {formatDate(log.timestamp)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handlePromptToSuggestion(log.question)}
                                                            className="text-foreground/40 hover:text-primary"
                                                            title="Añadir como sugerencia"
                                                            data-testid={`prompt-to-suggestion-${i}`}
                                                        >
                                                            <Lightbulb className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => setLogToDelete(log)}
                                                            className="text-foreground/40 hover:text-destructive"
                                                            title="Borrar prompt"
                                                            data-testid={`delete-chat-log-${i}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {chatLogsTotalPages > 1 && (
                                    <div className="flex items-center justify-between mt-4">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={chatLogsPage <= 1}
                                            onClick={() => fetchChatLogs(chatLogsPage - 1, chatLogsSearch)}
                                            data-testid="chat-logs-prev-btn"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <span className="text-sm text-foreground/70">Page {chatLogsPage} of {chatLogsTotalPages}</span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={chatLogsPage >= chatLogsTotalPages}
                                            onClick={() => fetchChatLogs(chatLogsPage + 1, chatLogsSearch)}
                                            data-testid="chat-logs-next-btn"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Suggestions Tab */}
                    <TabsContent value="suggestions">
                        <Card className="bg-card border-border">
                            <CardHeader>
                                <CardTitle className="text-foreground text-lg">Chat Suggestions</CardTitle>
                                <p className="text-sm text-foreground/50 mt-1">
                                    Estas sugerencias aparecen como botones en la pantalla inicial del chat. Si no hay ninguna, se muestran prompts aleatorios del historial.
                                </p>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleAddSuggestion} className="flex gap-2 mb-6">
                                    <Input
                                        placeholder="Escribe una nueva sugerencia (ej. Dame una rutina de pierna)"
                                        value={newSuggestion}
                                        onChange={(e) => setNewSuggestion(e.target.value)}
                                        maxLength={200}
                                        className="flex-1"
                                        data-testid="suggestion-input"
                                    />
                                    <Button type="submit" disabled={addingSuggestion || !newSuggestion.trim()} data-testid="add-suggestion-btn">
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Añadir
                                    </Button>
                                </form>

                                {suggestions.length === 0 ? (
                                    <p className="text-center text-foreground/50 py-8" data-testid="no-suggestions-msg">
                                        Aún no hay sugerencias. Añade la primera arriba.
                                    </p>
                                ) : (
                                    <div className="space-y-2" data-testid="suggestions-list">
                                        {suggestions.map((s, i) => (
                                            <div
                                                key={s.id}
                                                className="flex items-center justify-between gap-3 bg-background border border-border rounded-lg px-4 py-3"
                                                data-testid={`suggestion-row-${i}`}
                                            >
                                                <span className="text-foreground text-sm">{s.text}</span>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDeleteSuggestion(s.id)}
                                                    className="shrink-0 text-foreground/40 hover:text-destructive"
                                                    title="Borrar sugerencia"
                                                    data-testid={`delete-suggestion-${i}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Login History Tab */}
                    <TabsContent value="logs">
                        <Card className="bg-card border-border">
                            <CardHeader>
                                <CardTitle className="text-foreground flex items-center gap-2">
                                    <Clock className="w-5 h-5" />
                                    Login History
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loginLogs.length === 0 ? (
                                    <p className="text-center text-foreground/70 py-8">No login history yet</p>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="text-foreground/70">User</TableHead>
                                                        <TableHead className="text-foreground/70 hidden sm:table-cell">Email</TableHead>
                                                        <TableHead className="text-foreground/70">Login Time</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {loginLogs.map((log) => (
                                                        <TableRow key={log.id}>
                                                            <TableCell className="font-medium text-foreground">
                                                                {log.user_code}
                                                            </TableCell>
                                                            <TableCell className="text-foreground/70 hidden sm:table-cell">
                                                                {log.user_email || '-'}
                                                            </TableCell>
                                                            <TableCell className="text-foreground/70">
                                                                {formatDate(log.login_at)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {/* Pagination */}
                                        {logsTotalPages > 1 && (
                                            <div className="flex justify-center items-center gap-2 mt-4">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fetchLoginLogs(logsPage - 1)}
                                                    disabled={logsPage <= 1}
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </Button>
                                                <span className="text-sm text-foreground/70">
                                                    Page {logsPage} of {logsTotalPages}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fetchLoginLogs(logsPage + 1)}
                                                    disabled={logsPage >= logsTotalPages}
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Upload Tab */}
                    <TabsContent value="upload">
                        <Card className="bg-card border-border">
                            <CardHeader>
                                <CardTitle className="text-foreground flex items-center gap-2">
                                    <FileSpreadsheet className="w-5 h-5" />
                                    Upload BUNKER CODES
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-foreground/70 mb-4">
                                    Upload a CSV or Excel file with user codes. Required column: <code className="bg-secondary px-1 rounded">code</code> or <code className="bg-secondary px-1 rounded">User</code>. 
                                    Optional column: <code className="bg-secondary px-1 rounded">email</code> or <code className="bg-secondary px-1 rounded">Email</code>.
                                </p>
                                <p className="text-sm text-yellow-500 mb-4">
                                    ⚠️ Warning: Uploading will replace all existing non-admin users.
                                </p>
                                
                                <div className="flex gap-4">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv,.xls,.xlsx"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        id="file-upload"
                                    />
                                    <Button
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="gradient-gold text-primary-foreground"
                                        data-testid="upload-file-btn"
                                    >
                                        {uploading ? (
                                            <span className="flex items-center gap-2">
                                                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                                                Uploading...
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <Upload className="w-4 h-4" />
                                                Upload File
                                            </span>
                                        )}
                                    </Button>
                                    <Button
                                        onClick={handleExportExcel}
                                        variant="secondary"
                                        data-testid="download-excel-btn"
                                    >
                                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                                        Descargar Excel actual
                                    </Button>
                                </div>
                                <p className="text-xs text-foreground/50 mt-3">
                                    Descarga el archivo Excel con los usuarios y códigos de acceso actuales (mismo formato que la importación).
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            <AlertDialog open={!!logToDelete} onOpenChange={(open) => { if (!open) setLogToDelete(null); }}>
                <AlertDialogContent className="bg-card border-border" data-testid="delete-chat-log-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-foreground">¿Borrar este prompt?</AlertDialogTitle>
                        <AlertDialogDescription className="text-foreground/60">
                            Se eliminará permanentemente la entrada de <span className="text-foreground/80 font-medium">{logToDelete?.user_code}</span>:
                            <span className="block mt-2 italic text-foreground/70">"{logToDelete?.question}"</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-chat-log-cancel">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => handleDeleteChatLog(logToDelete?.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="delete-chat-log-confirm"
                        >
                            Borrar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminPanel;
